import { createContext, PropsWithChildren } from 'react';
import {
  ActionOutboxEntityType,
  CatalogResults,
  EventWithData,
  Listing,
  ListingWithCatalogData,
  PurchaseOrderTicketGroup,
  Sale,
  SaleWithCatalogData,
} from 'src/WebApiController';

export type SearchDataWithEvent = {
  [key: string]: {
    sales: SaleWithCatalogData[] | null;
    salesSummary: SaleWithCatalogData | null;
    listings: ListingWithCatalogData[] | null;
    listingsSummary: ListingWithCatalogData | null;
    ticketGroups: PurchaseOrderTicketGroup[] | null;
    ticketGroupsSummary: PurchaseOrderTicketGroup | null;
    failedToRetrieveData: boolean;
  };
};

export type SearchDataSummary = {
  salesSummary: SaleWithCatalogData | null;
  listingsSummary: ListingWithCatalogData | null;
  ticketGroupsSummary: PurchaseOrderTicketGroup | null;
};

export type EntitySearchResult = {
  data: SearchDataWithEvent;
  indexLastUpdatedAt?: string | null;
  totalItemsCount?: number | null;
  /**
   * PIT ID for next page requests, null if not using PIT (Point-In-Time) pagination
   */
  pitId?: string | null;
  /**
   * Search after values for next page cursor (null if no more pages)
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  searchAfter?: any[] | null;
};

export type SearchDataContextProviderProps<TQuery> = PropsWithChildren<{
  queryKey: string;
  entityType:
    | ActionOutboxEntityType.Listing
    | ActionOutboxEntityType.Sale
    | ActionOutboxEntityType.Purchase;
  getSearchData?: (
    ids: string[],
    filterQuery: TQuery
  ) => Promise<EntitySearchResult>;
  getSearchDataSummary?: (
    ids: string[],
    filterQuery: TQuery
  ) => Promise<SearchDataSummary>;
  disabled?: boolean;
}>;

export type ISearchDataContext = {
  isEnabled: boolean;
  isLoading: boolean;
  errorInfo?: { errorHeader: React.ReactNode; errorMessage: React.ReactNode };
  eventsTransformed?: EventWithData[];
  data?: CatalogResults;
  indexLastUpdatedAt?: string | null;
  totalItemsCount?: number | null;
  /**
   * PIT ID for next page requests, null if not using PIT (Point-In-Time) pagination
   */
  pitId?: string | null;
  /**
   * Search after values for next page cursor (null if no more pages)
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  searchAfter?: any[] | null;
  refetch: () => void;
  updateItemInEvent: (
    item: Listing | Sale | PurchaseOrderTicketGroup,
    entityType: ActionOutboxEntityType,
    sectionalUpdate?: boolean
  ) => void;
};

const emptyContext: ISearchDataContext = {
  isLoading: false,
  isEnabled: false,
  refetch: () => {},
  indexLastUpdatedAt: null,
  totalItemsCount: null,
  pitId: null,
  searchAfter: null,
  updateItemInEvent: () => {},
};

export const SearchDataContext =
  createContext<ISearchDataContext>(emptyContext);
